import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

/* Illustrative calls. Businesses are fictional; behaviors match shipped features. */
export const SCENES = [
  {
    biz: 'Bella Nails & Spa', kind: 'Salon', time: '9:47 PM',
    lines: [
      ['caller', 'Hi, can I get a gel fill Saturday morning?'],
      ['agent', 'Saturday at 10:30 is open with Linh. Want me to book it?'],
      ['caller', 'Yes, please.'],
    ],
    outcomes: ['Booked · Sat 10:30 AM', 'Confirmation texted', 'Owner notified'],
  },
  {
    biz: 'Northside Heating & Air', kind: 'HVAC', time: '11:12 PM',
    lines: [
      ['caller', 'Our furnace just stopped and it is freezing in here.'],
      ['agent', 'I have flagged this as urgent. Can a technician come at 8 AM?'],
      ['caller', 'Yes. Thank you.'],
    ],
    outcomes: ['Emergency flagged', 'Visit booked · 8:00 AM', 'Owner texted'],
  },
  {
    biz: 'Harbor Realty', kind: 'Real estate', time: '6:05 AM',
    lines: [
      ['caller', 'Is the house on Maple Street still available?'],
      ['agent', 'It is. I can show it tomorrow at 5:30. Are you pre-approved?'],
      ['caller', 'Not yet. 5:30 works.'],
    ],
    outcomes: ['Lead qualified', 'Showing on calendar', 'Follow-up set'],
  },
];

// Timeline (ms) for one call. Reduced motion skips straight to the final frame.
const T_RING = 1500;
const T_LINE = 1700;
const T_OUTCOME = 450;
const T_HOLD = 3600;


function stepDelay(s, step) {
  const n = s.lines.length;
  const m = s.outcomes.length;
  if (step === 0) return T_RING;
  if (step < n) return T_LINE;
  if (step === n) return 900;
  if (step < n + m) return T_OUTCOME;
  return T_HOLD;
}

/** Advances one step at a time, so hover-pause resumes exactly where it stopped. */
function useCallTimeline(reduced, paused) {
  const [scene, setSceneRaw] = useState(0);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (reduced || paused) return undefined;
    const s = SCENES[scene];
    const last = s.lines.length + s.outcomes.length;
    const id = setTimeout(() => {
      if (step >= last) {
        setSceneRaw((x) => (x + 1) % SCENES.length);
        setStep(0);
      } else {
        setStep(step + 1);
      }
    }, stepDelay(s, step));
    return () => clearTimeout(id);
  }, [scene, step, paused, reduced]);

  // Choosing an example shows that call complete, then the loop carries on.
  const setScene = (i) => {
    setSceneRaw(i);
    setStep(SCENES[i].lines.length + SCENES[i].outcomes.length);
  };
  return { scene, setScene, step: reduced ? 99 : step };
}

/** Canvas "voice aperture": radial ticks whose length follows the conversation's energy. */
function useAperture(canvasRef, stateRef, reduced) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    let raf = 0;
    let running = false;
    let visible = true;
    let w = 0;
    let h = 0;
    let energy = 0.2;
    const N = 132;
    // Colors come from the theme tokens, re-read whenever <html> switches light/dark.
    const colors = { wave: '56,189,248', ember: '255,180,84', orbit: '226,232,240' };
    const readColors = () => {
      const cs = getComputedStyle(canvas);
      const v = (name, fb) => (cs.getPropertyValue(name).trim() || fb).replace(/\s+/g, '');
      colors.wave = v('--signal-rgb', colors.wave);
      colors.ember = v('--ember-rgb', colors.ember);
      colors.orbit = v('--paper-rgb', colors.orbit);
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const frame = (now) => {
      const t = now / 1000;
      const { phase, speaker } = stateRef.current;
      const target = phase === 'ringing' ? 0.55 + 0.35 * Math.max(0, Math.sin(t * 9)) : speaker === 'agent' ? 0.95 : speaker === 'caller' ? 0.6 : 0.28;
      energy += (target - energy) * 0.08;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h * 0.34;
      const R = Math.min(w, h) * 0.24;
      const ring = phase === 'ringing';
      ctx.lineCap = 'round';
      for (let i = 0; i < N; i += 1) {
        const a = (i / N) * Math.PI * 2;
        const n = Math.sin(a * 3 + t * 1.3) * 0.5 + Math.sin(a * 7 - t * 2.2) * 0.3 + Math.sin(a * 13 + t * 3.9) * 0.2;
        const len = 3 + (n * 0.5 + 0.5) * energy * R * 0.62;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        const alpha = 0.25 + (n * 0.5 + 0.5) * 0.65;
        ctx.strokeStyle = `rgba(${ring ? colors.ember : colors.wave},${alpha})`;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(cx + cos * R, cy + sin * R);
        ctx.lineTo(cx + cos * (R + len), cy + sin * (R + len));
        ctx.stroke();
      }
      // Two fine orbit rings for depth
      ctx.strokeStyle = `rgba(${colors.orbit},0.08)`;
      ctx.lineWidth = 1;
      [1.75, 2.25].forEach((k) => { ctx.beginPath(); ctx.arc(cx, cy, R * k, 0, Math.PI * 2); ctx.stroke(); });
      if (running) raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (running || reduced || !visible || document.hidden) return;
      running = true;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => { running = false; cancelAnimationFrame(raf); };

    resize();
    readColors();
    if (reduced) frame(4000); else start();

    const mo = new MutationObserver(() => { readColors(); if (!running) frame(reduced ? 4000 : performance.now()); });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    const onResize = () => { resize(); if (!running) frame(reduced ? 4000 : performance.now()); };
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (ro) ro.observe(canvas); else window.addEventListener('resize', onResize);
    const io = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([e]) => { visible = e.isIntersecting; visible ? start() : stop(); })
      : null;
    if (io) io.observe(canvas);
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      if (ro) ro.disconnect(); else window.removeEventListener('resize', onResize);
      if (io) io.disconnect();
      mo.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [canvasRef, stateRef, reduced]);
}

export default function CallScene({ reduced }) {
  const [paused, setPaused] = useState(false);
  const { scene, setScene, step } = useCallTimeline(reduced, paused);
  const s = SCENES[scene];
  const shownLines = Math.min(step, s.lines.length);
  const shownOutcomes = Math.max(0, Math.min(step - s.lines.length, s.outcomes.length));
  const phase = step === 0 ? 'ringing' : step > s.lines.length ? 'done' : 'talking';
  const speaker = phase === 'talking' ? s.lines[shownLines - 1][0] : null;

  const canvasRef = useRef(null);
  const stateRef = useRef({ phase, speaker });
  stateRef.current = { phase, speaker };
  useAperture(canvasRef, stateRef, reduced);

  return (
    <figure
      className="a9l-scene"
      data-phase={phase}
      data-testid="hero-call-scene"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <canvas ref={canvasRef} aria-hidden="true" />
      <div className="a9l-scene__core" aria-hidden="true" />
      <figcaption className="a9l-sr">Example of Agent9 answering an after-hours call for a {s.kind.toLowerCase()} business.</figcaption>

      <div className="a9l-call">
        <div className="a9l-call__head">
          <span className="a9l-call__status" style={{ color: phase === 'ringing' ? 'var(--ember)' : 'var(--signal)' }}>
            <span className={`a9l-dot ${phase === 'ringing' ? 'a9l-dot--ember' : ''}`} aria-hidden="true" />
            {phase === 'ringing' ? 'Incoming call' : phase === 'done' ? 'Call handled' : 'Agent9 answered'}
          </span>
          <span className="a9l-label">{s.time}</span>
        </div>
        <div className="a9l-call__biz">{s.biz}</div>
        <div className="a9l-call__meta">{s.kind} · after hours</div>

        <div className="a9l-call__log">
          {s.lines.slice(0, shownLines).map(([who, text], i) => (
            <div key={`${scene}-${i}`} className={`a9l-msg a9l-msg--${who} a9l-step-in`}>
              <span className="a9l-msg__who">{who === 'agent' ? 'Agent9' : 'Caller'}</span>
              <span className="a9l-msg__text">{text}</span>
            </div>
          ))}
        </div>

        <div className="a9l-call__outcomes">
          {s.outcomes.slice(0, shownOutcomes).map((o) => (
            <span key={`${scene}-${o}`} className="a9l-outcome a9l-step-in">
              <Check size={12} aria-hidden="true" /> {o}
            </span>
          ))}
        </div>

        <div className="a9l-call__foot">
          <span className="a9l-label">Illustrative call</span>
          <div className="a9l-scene__dots" role="group" aria-label="Choose an example call">
            {SCENES.map((x, i) => (
              <button
                key={x.biz}
                type="button"
                className="a9l-scene__dot"
                aria-pressed={i === scene}
                aria-label={`Show ${x.kind.toLowerCase()} call example`}
                data-testid={`hero-scene-${i}`}
                onClick={() => setScene(i)}
              >
                <span />
              </button>
            ))}
          </div>
        </div>
      </div>
    </figure>
  );
}
